// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { getStatusCode } from "@insite/client-framework/ServerSideRendering";
import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import { getProductIds } from "@insite/client-framework/Services/ProductCompareService";
import { getProductCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    key: string;
    productIds: string[];
}

export interface GetProductsParameter extends ApiParameter {
    productIds: string[];
}

interface Props {
    apiParameter: GetProductsParameter;
    product: ProductModel;
    productsInfo: ProductInfo[];
    pricingLoaded?: boolean;
    skipPricingAndInventory: boolean;
    pids: string[];
    apiResult: ProductCollectionModel;
}

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = (props: { apiParameter: any; parameter: any }) => {
    props.apiParameter = props.parameter;
};

export const DispatchBeginLoadProducts: HandlerType = props => {
    props.dispatch({
        type: "xProductBom/isProductsLoading" as any,
    });
};

export const RequestDataFromProductsApi: HandlerType = async props => {
    props.apiResult = await getProductCollectionV2({ productIds: props.parameter.productIds });
};

export const DispatchCompleteLoadProducts: HandlerType = props => {
    let products = {};
    props?.apiResult?.products?.forEach(item => {
        const erpNumber = item.productNumber;
        products = {
            [erpNumber]: item,
            ...products,
        };
    });

    props.dispatch({
        type: "xProductBom/CompleteLoadProducts",
        products,
        key: props.parameter.key,
    } as any);

    let prodsInfo = [] as ProductInfo[];
    const pids: any = [];
    if (props.apiResult.products && props.apiResult.products.length > 0) {
        prodsInfo = props.apiResult.products.map((item: any) => {
            pids.push(item.id);
            const productInfo = createFromProduct(item);
            (productInfo as any).erpNumber = item.productNumber;
            return productInfo;
        });
    }
    if (prodsInfo.length > 0) {
        props.productsInfo = prodsInfo;
        props.pids = pids;
    }
};

export const LoadPricing: HandlerType = async props => {
    if (props.skipPricingAndInventory || !props.getState().context.session.isAuthenticated) {
        return;
    }

    let pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productsInfo,
            onSuccess: realTimePricing => {
                if (realTimePricing) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const pricing = realTimePricing.realTimePricingResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, pricing };
                    });
                }
                pricingLoaded = true;
            },
            onError: () => {
                pricingLoaded = true;
                // solving error thrown when failedToLoadPricing
                props.productsInfo = props.productsInfo.map(item => {
                    return { ...item, failedToLoadPricing: true };
                });
                SetProductInfo(props);
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
    await waitFor(() => pricingLoaded);
};

export const LoadInventory: HandlerType = async props => {
    if (props.skipPricingAndInventory || !props.getState().context.session.isAuthenticated) {
        return;
    }
    const pids = props.pids;
    if (!pids || pids.length === 0) {
        return;
    }

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: pids,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productsInfo) {
                        props.productsInfo &&
                            props.productsInfo.forEach(item => {
                                item.failedToLoadInventory = true;
                            });
                    }
                } else if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, inventory };
                    });
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const SetProductInfo: HandlerType = props => {
    if (props.productsInfo) {
        let productsInfo = {};
        props.productsInfo.forEach(item => {
            const erpNumber = (item as any).erpNumber;
            productsInfo = {
                [erpNumber]: item,
                ...productsInfo,
            };
        });

        props.dispatch({
            type: "xProductBom/CompleteLoadProductsInfo",
            productsInfo,
            key: props.parameter.key,
        } as any);
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginLoadProducts,
    RequestDataFromProductsApi,
    DispatchCompleteLoadProducts,
    LoadPricing,
    LoadInventory,
    SetProductInfo,
];

const getProducts = createHandlerChainRunner(chain, "getProducts");
export default getProducts;
